import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { transformToolResultToActionCard, createErrorActionCard } from '@/utils/copilot/responseProjection';
import type { ResponseProjection } from '@/types/mcpTool';
import type { CopilotToolRegistry } from '@/types/copilot';
import type { Message, StreamResponse } from './chatTypes';

interface HandleStreamEventArgs {
  data: StreamResponse;
  currentEvent: string;
  assistantMessageId: string;
  accumulatedContent: string;
  hasRenderedFormattedResponseRef: MutableRefObject<boolean>;
  setMessages: Dispatch<SetStateAction<Message[]>>;
  setStatus: Dispatch<SetStateAction<string>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  updateCtxVersionState: (nextVersion: string | null | undefined) => void;
  handleToolRegistryUpdate: (registry: CopilotToolRegistry | null | undefined) => void;
  getResponseProjection: (key: string) => ResponseProjection | undefined;
  updateStreamToken?: (token: string | null, expiresAt?: string | null) => void;
}

interface HandleStreamEventResult {
  accumulatedContent: string;
}

const resolveDisplayName = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  if (value && typeof value === 'object') {
    const candidate =
      (value as any).name ??
      (value as any).action_name ??
      (value as any).display_name ??
      (value as any).title;

    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }

  if (value !== undefined && value !== null) {
    return String(value);
  }

  return fallback;
};

export const handleStreamEvent = ({
  data,
  currentEvent,
  assistantMessageId,
  accumulatedContent,
  hasRenderedFormattedResponseRef,
  setMessages,
  setStatus,
  setIsLoading,
  updateCtxVersionState,
  handleToolRegistryUpdate,
  getResponseProjection,
  updateStreamToken,
}: HandleStreamEventArgs): HandleStreamEventResult => {
  const ctxVersionCandidate =
    data.ctx_version ??
    data.result?.ctx_version ??
    data.tool_result?.ctx_version ??
    data.tool_context?.ctx_version ??
    data.tool_registry?.ctx_version;

  if (ctxVersionCandidate !== undefined) {
    updateCtxVersionState(typeof ctxVersionCandidate === 'string' ? ctxVersionCandidate : null);
  }

  if (data.tool_registry) {
    handleToolRegistryUpdate(data.tool_registry);
  }

  if (updateStreamToken) {
    const candidateToken =
      typeof data.stream_token === 'string'
        ? data.stream_token
        : typeof (data as any)?.result?.stream_token === 'string'
          ? (data as any).result.stream_token
          : typeof (data as any)?.tool_result?.stream_token === 'string'
            ? (data as any).tool_result.stream_token
            : typeof (data as any)?.token_info?.stream_token === 'string'
              ? (data as any).token_info.stream_token
              : null;

    const candidateExpiry =
      typeof data.stream_token_expires_at === 'string'
        ? data.stream_token_expires_at
        : typeof (data as any)?.token_info?.stream_token_expires_at === 'string'
          ? (data as any).token_info.stream_token_expires_at
          : typeof (data as any)?.result?.stream_token_expires_at === 'string'
            ? (data as any).result.stream_token_expires_at
            : undefined;

    if (candidateToken || candidateExpiry) {
      updateStreamToken(candidateToken, candidateExpiry ?? null);
    }
  }

  if (data.type === 'stream_token') {
    const tokenPayload = (data as any) || {};
    updateStreamToken?.(
      typeof tokenPayload.stream_token === 'string'
        ? tokenPayload.stream_token
        : typeof tokenPayload.token === 'string'
          ? tokenPayload.token
          : null,
      typeof tokenPayload.stream_token_expires_at === 'string'
        ? tokenPayload.stream_token_expires_at
        : typeof tokenPayload.expires_at === 'string'
          ? tokenPayload.expires_at
          : null
    );
    return { accumulatedContent };
  }

  if (data.type === 'start') {
    setStatus('Thinking...');
    return { accumulatedContent };
  }

  if (data.type === 'status' || currentEvent === 'status') {
    const statusMessage =
      typeof data.message === 'string'
        ? data.message
        : typeof data.status?.message === 'string'
          ? data.status.message
          : '';

    if (statusMessage) {
      setStatus(statusMessage);
    }
    return { accumulatedContent };
  }

  if (data.type === 'token' && data.content) {
    const nextContent = accumulatedContent + data.content;

    setMessages(prev => {
      const existing = prev.find(m => m.id === assistantMessageId);
      if (existing) {
        return prev.map(m =>
          m.id === assistantMessageId
            ? { ...m, content: nextContent }
            : m
        );
      }

      return [
        ...prev,
        {
          id: assistantMessageId,
          role: 'assistant',
          content: nextContent,
          createdAt: new Date().toISOString(),
        },
      ];
    });

    return { accumulatedContent: nextContent };
  }

  if (data.type === 'complete') {
    const completeActionRequired = data.tool_result?.result?.action_required;
    if (completeActionRequired === 'oauth_authorization' || completeActionRequired === 'connect_oauth') {
      const oauthMessage: Message = {
        id: `oauth-${Date.now()}`,
        role: 'oauth',
        content: data.tool_result?.result?.message || 'Authorization required',
        createdAt: new Date().toISOString(),
        oauthData: {
          tool: data.tool_result?.result?.tool || data.tool_result?.tool?.name || 'Unknown Tool',
          action: data.tool_result?.result?.action || 'Unknown Action',
          authUrl: data.tool_result?.result?.auth_url || '',
          message: data.tool_result?.result?.message || 'Please authorize this tool to continue',
          actionRequired: completeActionRequired,
        },
      };

      setMessages(prev => [...prev, oauthMessage]);
      setStatus('Authorization required');
    } else if (data.content) {
      // Always update the assistant message with the synthesized content from
      // the complete event. When execution_details are already attached (from
      // a preceding tool_result event), this adds the LLM-synthesized summary
      // as the main readable content alongside the collapsible details.
      setMessages(prev => {
        const existing = prev.find(m => m.id === assistantMessageId);
        if (existing) {
          return prev.map(m =>
            m.id === assistantMessageId
              ? { ...m, content: data.content || '' }
              : m
          );
        }

        return [
          ...prev,
          {
            id: assistantMessageId,
            role: 'assistant' as const,
            content: data.content || '',
            createdAt: new Date().toISOString(),
          },
        ];
      });
    }

    setStatus('');
    return { accumulatedContent };
  }

  if (data.type === 'tool_context') {
    if (data.tool_context || data.tool_registry) {
      handleToolRegistryUpdate(data.tool_context || data.tool_registry);
    }
    setStatus('Loading tools...');
    return { accumulatedContent };
  }

  if (data.type === 'action_detected') {
    const toolName = data.data?.tool_name || 'tool';
    const actionName = data.data?.action_name || 'action';
    setStatus(`Executing ${toolName}.${actionName}...`);
    return { accumulatedContent };
  }

  if (data.type === 'tool_result') {
    if (data.result?.tool_registry || data.tool_result?.tool_registry) {
      handleToolRegistryUpdate(data.result?.tool_registry || data.tool_result?.tool_registry);
    }

    const actionRequired = data.result?.action_required;
    if (actionRequired === 'oauth_authorization' || actionRequired === 'connect_oauth') {
      const oauthMessage: Message = {
        id: `oauth-${Date.now()}`,
        role: 'oauth',
        content: data.result?.message || 'Authorization required',
        createdAt: new Date().toISOString(),
        oauthData: {
          tool: data.result?.tool || data.tool_name || 'Unknown Tool',
          action: data.result?.action || 'Unknown Action',
          authUrl: data.result?.auth_url || '',
          message: data.result?.message || 'Please authorize this tool to continue',
          actionRequired: actionRequired,
        },
      };

      setMessages(prev => [...prev, oauthMessage]);
      setStatus('Authorization required');
      return { accumulatedContent };
    }

    if (data.result?.result?.formatted_response || data.result?.result?.response) {
      const toolName = resolveDisplayName(
        data.result?.tool?.name ?? data.tool_name ?? data.tool_result?.tool?.name ?? data.tool_result?.tool,
        'Unknown Tool'
      );
      const actionName = resolveDisplayName(
        data.result?.action?.name ?? data.result?.action ?? data.tool_result?.action?.name ?? data.tool_result?.action,
        'Unknown Action'
      );

      // Store execution details on the assistant message — the complete event
      // will supply the synthesized text as the main content.
      setMessages(prev => {
        const existing = prev.find(m => m.id === assistantMessageId);
        const executionDetail = {
          tool_name: toolName,
          action_name: actionName,
          raw_response: data.result.result.response,
        };
        if (existing) {
          return prev.map(m =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  execution_details: [...(m.execution_details || []), executionDetail],
                }
              : m
          );
        }
        return [
          ...prev,
          {
            id: assistantMessageId,
            role: 'assistant' as const,
            content: '',
            createdAt: new Date().toISOString(),
            execution_details: [executionDetail],
          },
        ];
      });

      setStatus('');
      hasRenderedFormattedResponseRef.current = true;
      return { accumulatedContent };
    }

    const toolName = resolveDisplayName(
      data.tool_name ?? data.result?.tool?.name ?? data.result?.tool,
      'Unknown Tool'
    );
    const actionName = resolveDisplayName(
      data.result?.action?.name ?? data.result?.action,
      'Unknown Action'
    );
    const projectionKey = `${toolName}.${actionName}`;
    const responseProjection = getResponseProjection(projectionKey);

    if (responseProjection && data.result) {
      try {
        const actionCardData = transformToolResultToActionCard(
          data.result,
          responseProjection,
          toolName,
          actionName
        );

        const toolResultMessage: Message = {
          id: `tool-result-${Date.now()}`,
          role: 'tool_result',
          content: `Executed: ${toolName}.${actionName}`,
          createdAt: new Date().toISOString(),
          action_card_data: actionCardData,
        };

        setMessages(prev => [...prev, toolResultMessage]);
      } catch (error) {
        const errorCardData = createErrorActionCard(
          toolName,
          actionName,
          error instanceof Error ? error.message : 'Transformation failed'
        );

        const errorMessage: Message = {
          id: `tool-result-error-${Date.now()}`,
          role: 'tool_result',
          content: `Error executing ${toolName}.${actionName}`,
          createdAt: new Date().toISOString(),
          action_card_data: errorCardData,
        };

        setMessages(prev => [...prev, errorMessage]);
      }
    }

    return { accumulatedContent };
  }

  if (data.type === 'plan' && data.steps && Array.isArray(data.steps)) {
    const planSteps = data.steps.map((step: any, index: number) => ({
      name: step.name || step.description || `Step ${index + 1}`,
      description: step.description || step.name || '',
      status: (step.status as 'pending' | 'running' | 'completed' | 'failed') || 'pending',
      tool_name: step.tool_name || step.tool || undefined,
    }));

    setMessages(prev => {
      const existing = prev.find(m => m.id === assistantMessageId);
      if (existing) {
        return prev.map(m =>
          m.id === assistantMessageId
            ? { ...m, plan_steps: planSteps }
            : m
        );
      }
      return [
        ...prev,
        {
          id: assistantMessageId,
          role: 'assistant' as const,
          content: '',
          createdAt: new Date().toISOString(),
          plan_steps: planSteps,
        },
      ];
    });

    setStatus('Executing plan...');
    return { accumulatedContent };
  }

  if (data.type === 'execution' && data.data) {
    // Update plan step status when execution events arrive
    const stepName = data.data.step_name || data.data.name;
    const stepStatus = data.data.status;
    if (stepName && stepStatus) {
      setMessages(prev =>
        prev.map(m => {
          if (m.id !== assistantMessageId || !m.plan_steps) return m;
          return {
            ...m,
            plan_steps: m.plan_steps.map(s =>
              s.name === stepName || s.description === stepName
                ? { ...s, status: stepStatus }
                : s
            ),
          };
        })
      );
    }
    return { accumulatedContent };
  }

  if (data.type === 'end' || data.type === 'done') {
    setStatus('');
    setIsLoading(false);
    return { accumulatedContent };
  }

  return { accumulatedContent };
};
